import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CardGiftcardRounded from "@mui/icons-material/CardGiftcardRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import Loyalty, { BalanceTransaction } from "../../../api/Loyalty";
import { useCookies } from "react-cookie";

interface LoyaltySectionProps {
    userKey: string;
}

const TX_TYPE_LABELS: Record<string, string> = {
    referral_credit: "Referral reward",
    booking_debit: "Booking payment",
    cashback_credit: "Cashback",
    expiry_debit: "Expired",
    manual_credit: "Manual credit",
    manual_debit: "Manual debit",
};

const LoyaltySection: React.FC<LoyaltySectionProps> = ({ userKey }) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [balance, setBalance] = useState<number>(0);
    const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustAmount, setAdjustAmount] = useState("");
    const [adjustNote, setAdjustNote] = useState("");
    const [adjustLoading, setAdjustLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!uuid || !brand_id || !userKey) return;
        setLoading(true);
        try {
            const [balData, txData] = await Promise.all([
                Loyalty.getUserBalance(uuid, brand_id, userKey),
                Loyalty.getUserTransactions(uuid, brand_id, userKey, 0, 5),
            ]);
            setBalance(balData?.balance ?? 0);
            setTransactions(txData?.items ?? []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [uuid, brand_id, userKey]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleAdjust = async () => {
        const amount = Number(adjustAmount);
        if (!amount || !adjustNote.trim()) return;
        setAdjustLoading(true);
        try {
            await Loyalty.adjustBalance(uuid, brand_id, userKey, amount, adjustNote.trim());
            setAdjustOpen(false);
            setAdjustAmount("");
            setAdjustNote("");
            loadData();
        } catch (err) {
            console.error(err);
        } finally {
            setAdjustLoading(false);
        }
    };

    if (loading) {
        return (
            <Stack alignItems="center" py={4}>
                <Typography color="text.secondary" fontSize="14px">Loading...</Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={2} px={2} py={2}>
            {/* Balance card */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    p: 2,
                    borderRadius: "16px",
                    bgcolor: alpha(primary, 0.06),
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: alpha(primary, 0.12),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                    }}
                >
                    <CardGiftcardRounded sx={{ fontSize: 20, color: primary }} />
                </Box>
                <Stack flex={1}>
                    <Typography fontSize="11px" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing="0.5px">
                        Loyalty Balance
                    </Typography>
                    <Typography fontSize="22px" fontWeight={700} color="text.primary">
                        {balance.toFixed(2)}
                    </Typography>
                </Stack>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAdjustOpen(true)}
                    sx={{
                        borderColor: alpha(primary, 0.3),
                        color: primary,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        borderRadius: "10px",
                    }}
                >
                    Adjust
                </Button>
            </Stack>

            {/* Recent transactions */}
            <Stack spacing={0.5}>
                <Typography fontSize="11px" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing="0.5px" px={0.5}>
                    Recent Transactions
                </Typography>
                {transactions.length === 0 ? (
                    <Typography fontSize="13px" color="text.secondary" px={0.5} py={1}>
                        No transactions yet.
                    </Typography>
                ) : (
                    transactions.map((tx) => (
                        <Stack
                            key={tx.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                py: 1,
                                px: 1,
                                borderRadius: "10px",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                            }}
                        >
                            <Stack spacing={0}>
                                <Typography fontSize="13px" fontWeight={500}>
                                    {TX_TYPE_LABELS[tx.type] ?? tx.type}
                                </Typography>
                                {tx.note && (
                                    <Typography fontSize="11px" color="text.secondary" noWrap maxWidth={180}>
                                        {tx.note}
                                    </Typography>
                                )}
                            </Stack>
                            <Stack alignItems="flex-end">
                                <Typography
                                    fontSize="14px"
                                    fontWeight={600}
                                    color={tx.amount > 0 ? "success.main" : "error.main"}
                                >
                                    {tx.amount > 0 ? "+" : ""}{tx.amount.toFixed(2)}
                                </Typography>
                                <Typography fontSize="10px" color="text.secondary">
                                    {new Date(tx.created).toLocaleDateString()}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))
                )}
            </Stack>

            {/* Adjust dialog */}
            <Dialog
                open={adjustOpen}
                onClose={() => setAdjustOpen(false)}
                PaperProps={{ sx: { borderRadius: "16px", maxWidth: 340 } }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: "16px" }}>
                    Adjust Balance
                </DialogTitle>
                <DialogContent sx={{ overflowX: "hidden" }}>
                    <Stack spacing={2} pt={1}>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant={Number(adjustAmount) > 0 || adjustAmount === "" ? "contained" : "outlined"}
                                size="small"
                                startIcon={<AddRounded />}
                                onClick={() => {
                                    const abs = Math.abs(Number(adjustAmount) || 0);
                                    setAdjustAmount(abs ? String(abs) : "");
                                }}
                                sx={{ flex: 1, textTransform: "none", borderRadius: "10px" }}
                            >
                                Credit
                            </Button>
                            <Button
                                variant={Number(adjustAmount) < 0 ? "contained" : "outlined"}
                                size="small"
                                color="error"
                                startIcon={<RemoveRounded />}
                                onClick={() => {
                                    const abs = Math.abs(Number(adjustAmount) || 0);
                                    setAdjustAmount(abs ? String(-abs) : "-");
                                }}
                                sx={{ flex: 1, textTransform: "none", borderRadius: "10px" }}
                            >
                                Debit
                            </Button>
                        </Stack>
                        <TextField
                            label="Amount (cents)"
                            type="number"
                            value={adjustAmount}
                            onChange={(e) => setAdjustAmount(e.target.value)}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Note (required)"
                            value={adjustNote}
                            onChange={(e) => setAdjustNote(e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAdjustOpen(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAdjust}
                        variant="contained"
                        disabled={!Number(adjustAmount) || !adjustNote.trim() || adjustLoading}
                        sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                        {adjustLoading ? "Saving..." : "Apply"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default LoyaltySection;
